import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAPIWrapper } from 'drf-react-by-schema';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

import { useAppSelector } from 'src/app/hooks';
import { getDecimal } from 'src/utils/utils';

import { Layout } from 'src/styles';
import {
  getTotalsMesocarpoProcessingLabel,
  HomeTotalsEntrepostoType,
  TotalsMesocarpoProcessing,
} from 'src/@types';
import { FlocosProductionStatus, getFlocosProductionLabel } from 'src/constants';
import { ProductionTimeRange } from 'src/constants/ProductionTimeRange';
import { getProcessingKeyLabel, ProcessingKey } from 'src/constants/ProcessingKey';

import HomeTotalsCard from './HomeTotalsCard';
import HomeTotalsCardGroup from './HomeTotalsCardGroup';

export default function HomeTotalsEntreposto() {
  const { perfil, isMobile } = useAppSelector((state) => state.page);
  const { getRawData } = useAPIWrapper();
  const location = useLocation();

  const [totals, setTotals] = useState<HomeTotalsEntrepostoType>();
  const [timeRange, setTimeRange] = useState<ProductionTimeRange>(ProductionTimeRange.ALL);

  const [pendentes, pendentesStr] = useMemo(() => {
    if (!totals || !totals[ProcessingKey.FLOCOS] || !perfil) {
      return [undefined, undefined];
    }

    const totalsFlocosReadyToReview = totals[ProcessingKey.FLOCOS]['ready_to_review_lotes'];
    const totalsFlocosReviewed = totals[ProcessingKey.FLOCOS]['reviewed_lotes'];

    const newPendentes = totalsFlocosReadyToReview.length + totalsFlocosReviewed.length;

    const pendentesStrAr: React.ReactElement[] = [];

    if (totalsFlocosReadyToReview.length > 0) {
      pendentesStrAr.push(
        <Link
          to={
            totalsFlocosReadyToReview.length === 1
              ? `/flocos_production/view/${totalsFlocosReadyToReview[0]}`
              : `/flocos_production/?status=${FlocosProductionStatus.READY_TO_REVIEW}`
          }>
          {totalsFlocosReadyToReview.length === 1
            ? '1 lote de flocos a revisar'
            : `${totalsFlocosReadyToReview.length} lotes de flocos a revisar`}
        </Link>
      );
    }

    if (totalsFlocosReviewed.length > 0) {
      pendentesStrAr.push(
        <Link
          to={
            totalsFlocosReviewed.length === 1
              ? `/flocos_production/view/${totalsFlocosReviewed[0]}`
              : `/flocos_production/?status=${FlocosProductionStatus.REVIEWED}`
          }>
          {totalsFlocosReviewed.length === 1
            ? '1 lote de flocos revisado e ainda não beneficiado'
            : `${totalsFlocosReviewed.length} lotes de flocos revisados e ainda não beneficiados`}
        </Link>
      );
    }

    const newPendentesStr = (
      <>
        Há{' '}
        {pendentesStrAr.map((pendente, index) => (
          <React.Fragment key={index}>
            {index > 0 && index === pendentesStrAr.length - 1 && ' e '}
            {index > 0 && index < pendentesStrAr.length - 1 && ', '}
            {pendente}
          </React.Fragment>
        ))}
        .
      </>
    );

    return [newPendentes, newPendentesStr];
  }, [totals, perfil]);

  useEffect(() => {
    if (!perfil) {
      return;
    }
    const retrieveData = async () => {
      const newTotals = await getRawData(`totals_entreposto/?time_range=${timeRange}`);
      setTotals(newTotals);
    };
    retrieveData();
  }, [perfil, timeRange, location]);

  if (!totals || !perfil?.entreposto) {
    return null;
  }

  return (
    <Box sx={Layout.homeTotalsInner}>
      <Typography variant="h6" color="Menu" sx={{ textAlign: 'center' }}>
        {perfil.entreposto.label}
      </Typography>
      {typeof pendentes !== 'undefined' && pendentes > 0 && (
        <Card>
          <CardContent sx={Layout.homeTotalsCardContent}>
            <Box sx={Layout.homeTotalsIconWrapper}>
              <NotificationsNoneIcon sx={Layout.homeTotalsIcon} />
            </Box>
            <Typography sx={{ textAlign: 'center' }}>{pendentesStr}</Typography>
          </CardContent>
        </Card>
      )}
      {Object.values(ProcessingKey).map((processingKey) => (
        <React.Fragment key={processingKey}>
          {!!totals[processingKey] && (
            <HomeTotalsCardGroup
              key={processingKey}
              label={getProcessingKeyLabel(processingKey)}
              timeRange={timeRange}
              setTimeRange={setTimeRange}
              hasTimeRangeMenu={processingKey === ProcessingKey.MESOCARPO}
              isMobile={isMobile}>
              {processingKey === ProcessingKey.MESOCARPO &&
                Object.keys(totals[processingKey]).map((key) => {
                  const totalKey = key as TotalsMesocarpoProcessing;
                  const total = totals[processingKey][totalKey];
                  return (
                    <HomeTotalsCard
                      key={totalKey}
                      label={getTotalsMesocarpoProcessingLabel({ key: totalKey })}
                      value={getDecimal(
                        total,
                        getTotalsMesocarpoProcessingLabel({ key: totalKey, unit: true })
                      )}
                      seeMoreTarget="/mesocarpo_processing"
                      isMobile={isMobile}
                    />
                  );
                })}
              {processingKey === ProcessingKey.FLOCOS &&
                Object.keys(totals[processingKey]).map((key) => {
                  if (['ready_to_review_lotes', 'reviewed_lotes'].includes(key)) {
                    return null;
                  }
                  const totalKey = key as FlocosProductionStatus;
                  const total = totals[processingKey][totalKey];
                  return (
                    <HomeTotalsCard
                      key={totalKey}
                      label={getFlocosProductionLabel({ status: totalKey })}
                      value={getDecimal(total, 'Kg')}
                      seeMoreTarget={`/flocos_production?status=${totalKey}`}
                      isMobile={isMobile}
                    />
                  );
                })}
            </HomeTotalsCardGroup>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}
